/**
 * IdentityGate.js — Zero-friction email routing at moovinforward.org
 *
 * User types their email → digital platform matches against the role tier
 * registry → auto-redirects to their scoped surface without additional
 * friction. Unmatched emails are offered the Institutional Activation Gate.
 *
 * Mounted on the landing page (App.js MainPage) so every visitor hits
 * their correct dashboard without navigating manually.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const NAVY = "#020A1F"; const INK = "#F5F5F7";

export default function IdentityGate({ compact = false }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const resolve = async (e) => {
    if (e) e.preventDefault();
    if (!email) return;
    setBusy(true); setStatus(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/identity/resolve`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const d = await r.json();
      setStatus(d);
      if (d.matched && d.redirect) {
        setTimeout(() => navigate(d.redirect), 350);
      }
    } catch (err) {
      setStatus({ matched: false, reason: "network_error" });
    }
    setBusy(false);
  };

  const wrapStyle = compact
    ? { padding: 0 }
    : { padding: "clamp(16px,3vw,28px)", background: "rgba(3,11,26,0.65)", borderRadius: 12, border: `1px solid ${CYAN}44` };

  return (
    <div style={wrapStyle} data-testid="identity-gate">
      {!compact && (
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 8 }}>
          ZERO-FRICTION IDENTITY ROUTING
        </div>
      )}
      <form onSubmit={resolve} style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input type="email" value={email} onChange={e => setEmail(e.target.value)}
          placeholder="your.email@company.com" data-testid="identity-gate-email"
          style={{
            flex: "1 1 220px", padding: "12px 14px",
            background: "rgba(0,0,0,0.4)", border: `1px solid ${CHROME}44`,
            color: INK, borderRadius: 8, fontSize: 14, fontFamily: "inherit",
          }} />
        <button type="submit" disabled={busy || !email} data-testid="identity-gate-submit"
          style={{
            padding: "12px 22px", background: CYAN, color: NAVY, border: 0,
            borderRadius: 8, fontWeight: 700, cursor: busy ? "wait" : "pointer",
            letterSpacing: "0.05em", fontSize: 13, textTransform: "uppercase",
            opacity: busy || !email ? 0.6 : 1,
          }}>
          {busy ? "Routing…" : "Enter Platform"}
        </button>
      </form>
      {status && (
        <div style={{ marginTop: 12, fontSize: 12, color: status.matched ? CYAN : CHROME }}
          data-testid="identity-gate-result">
          {status.matched
            ? `✓ Matched as ${status.persona} (${status.role}) · routing to ${status.redirect}…`
            : (
              <span>
                Email not registered.{" "}
                <a href="/activate" style={{ color: CYAN, textDecoration: "underline" }} data-testid="identity-gate-activate-link">
                  Execute the Universal Institutional Activation Gate →
                </a>
              </span>
            )}
        </div>
      )}
    </div>
  );
}
